import './ModalFiltro.css';
import React, { useRef, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import '../../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/** Restringe a lista pelo cadastro de veículos do SGA; undefined = todas. */
export type CadastroSgaFiltro = 'com' | 'sem' | undefined;

interface ModalFiltroProps {
  isOpen: boolean;
  onClose: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  onPreviewChange?: (startYear: number, startMonth: number, endYear: number, endMonth: number) => void;
  onApplyFilters: (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    order?: 'lucro' | 'prejuizo',
    resultadoMin?: number,
    resultadoMax?: number,
    rangeTouched?: boolean,
    searchAssociado?: string,
    cadastroSga?: CadastroSgaFiltro
  ) => void;
  initialStartYear?: number;
  initialStartMonth?: number;
  initialEndYear?: number;
  initialEndMonth?: number;
  initialOrder?: 'lucro' | 'prejuizo';
  initialResultadoMin?: number;
  initialResultadoMax?: number;
  initialRangeTouched?: boolean;
  rangeMinUi: number;
  rangeMaxUi: number;
  initialSearchAssociado?: string;
  initialCadastroSga?: CadastroSgaFiltro;
  /** RELACIONAMENTO nunca vê valores — esconde ordenação por resultado e range de valores */
  hideResultadoControls?: boolean;
}

const ModalFiltro: React.FC<ModalFiltroProps> = ({
  isOpen,
  onClose,
  sidebarRef,
  onApplyFilters,
  onPreviewChange,
  initialStartYear,
  initialStartMonth,
  initialEndYear,
  initialEndMonth,
  initialOrder,
  initialResultadoMin,
  initialResultadoMax,
  initialRangeTouched,
  rangeMinUi,
  rangeMaxUi,
  initialSearchAssociado,
  initialCadastroSga,
  hideResultadoControls,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const defaultDate = new Date();
  defaultDate.setDate(1);
  defaultDate.setMonth(defaultDate.getMonth() - 2);
  const defaultYear = defaultDate.getFullYear();
  const defaultMonth = defaultDate.getMonth() + 1;

  const [startYear, setStartYear] = useState<number>(initialStartYear ?? defaultYear);
  const [startMonth, setStartMonth] = useState<number>(initialStartMonth ?? defaultMonth);
  const [endYear, setEndYear] = useState<number>(initialEndYear ?? defaultYear);
  const [endMonth, setEndMonth] = useState<number>(initialEndMonth ?? defaultMonth);
  
  const [order, setOrder] = useState<'lucro' | 'prejuizo'>(initialOrder ?? 'lucro');
  
  const [rangeTouched, setRangeTouched] = useState<boolean>(initialRangeTouched ?? false);
  const [rangeMin, setRangeMin] = useState<number | undefined>(initialResultadoMin);
  const [rangeMax, setRangeMax] = useState<number | undefined>(initialResultadoMax);

  const [rangeMinEdit, setRangeMinEdit] = useState(false);
  const [rangeMaxEdit, setRangeMaxEdit] = useState(false);
  const [rangeMinText, setRangeMinText] = useState('');
  const [rangeMaxText, setRangeMaxText] = useState('');
  const [searchAssociado, setSearchAssociado] = useState<string>(initialSearchAssociado ?? '');
  const [cadastroSga, setCadastroSga] = useState<CadastroSgaFiltro>(initialCadastroSga);


  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() - 2);
  const maxYear = maxDate.getFullYear();
  const maxMonth = maxDate.getMonth() + 1; // 1-indexed

  const yearOptions: number[] = [];
  for (let y = 2025; y <= maxYear; y++) {
    yearOptions.push(y);
  }

  const ALLOWED_MONTHS = [
    { num: 1, name: 'Janeiro' },
    { num: 2, name: 'Fevereiro' },
    { num: 3, name: 'Março' },
    { num: 4, name: 'Abril' },
    { num: 5, name: 'Maio' },
    { num: 6, name: 'Junho' },
    { num: 7, name: 'Julho' },
    { num: 8, name: 'Agosto' },
    { num: 9, name: 'Setembro' },
    { num: 10, name: 'Outubro' },
    { num: 11, name: 'Novembro' },
    { num: 12, name: 'Dezembro' },
  ];

  useEffect(() => {
    if (isOpen) {
      setStartYear(initialStartYear ?? defaultYear);
      setStartMonth(initialStartMonth ?? defaultMonth);
      setEndYear(initialEndYear ?? defaultYear);
      setEndMonth(initialEndMonth ?? defaultMonth);
      setOrder(initialOrder ?? 'lucro');
      setRangeTouched(initialRangeTouched ?? false);
      setRangeMin(initialResultadoMin);
      setRangeMax(initialResultadoMax);
      setRangeMinText('');
      setRangeMaxText('');
      setRangeMinEdit(false);
      setRangeMaxEdit(false);
      setSearchAssociado(initialSearchAssociado ?? '');
      setCadastroSga(initialCadastroSga);
    }
  }, [isOpen, initialStartYear, initialStartMonth, initialEndYear, initialEndMonth, initialOrder, initialResultadoMin, initialResultadoMax, initialRangeTouched, initialSearchAssociado, initialCadastroSga, defaultYear, defaultMonth]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const formatarValor = (valor: number) => {
    const formatado = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Math.abs(valor));
    return valor < 0 ? `R$ -${formatado.replace('R$', '').trim()}` : formatado;
  };

  const parseBrlNumber = (s: string) => {
    const raw = String(s ?? '').trim();
    if (!raw) return NaN;

    const base = raw
      .replace(/R\$\s?/g, '')
      .replace(/\s/g, '')
      .replace(/[^\d,.-]/g, '');

    if (base.includes(',')) {
      return Number(base.replace(/\./g, '').replace(/,/g, '.'));
    }

    const parts = base.split('.');
    if (parts.length > 2) return Number(parts.join(''));
    return Number(base.replace(/,/g, ''));
  };

  const commitRangeMinText = () => {
    const n = parseBrlNumber(rangeMinText);
    if (!Number.isFinite(n)) {
      setRangeMinText(String(rangeMin ?? rangeMinUi));
      setRangeMinEdit(false);
      return;
    }
    const v = Math.min(Math.max(n, rangeMinUi), rangeMaxUi);
    setRangeTouched(true);
    setRangeMin(v);
    if (rangeMax !== undefined && v > rangeMax) setRangeMax(v);
    setRangeMinText(String(v));
    setRangeMinEdit(false);
  };

  const commitRangeMaxText = () => {
    const n = parseBrlNumber(rangeMaxText);
    if (!Number.isFinite(n)) {
      setRangeMaxText(String(rangeMax ?? rangeMaxUi));
      setRangeMaxEdit(false);
      return;
    }
    const v = Math.min(Math.max(n, rangeMinUi), rangeMaxUi);
    setRangeTouched(true);
    setRangeMax(v);
    if (rangeMin !== undefined && v < rangeMin) setRangeMin(v);
    setRangeMaxText(String(v));
    setRangeMaxEdit(false);
  };

  const handleResetRange = () => {
    setRangeTouched(false);
    setRangeMin(undefined);
    setRangeMax(undefined);
    setRangeMinEdit(false);
    setRangeMaxEdit(false);
    setRangeMinText('');
    setRangeMaxText('');
  };

  const handleApply = () => {
    const startDate = new Date(startYear, startMonth - 1, 1);
    const endDate = new Date(endYear, endMonth - 1, 1);

    if (startDate > endDate) {
      toast.error('A data final não pode ser anterior à data inicial.');
      return;
    }

    setTimeout(() => onApplyFilters(
      startYear,
      startMonth,
      endYear,
      endMonth,
      order,
      rangeMin,
      rangeMax,
      rangeTouched,
      searchAssociado,
      cadastroSga
    ), 0);
    onClose();
  };

  return (
    <div className="modal-filtro-overlay">
      <div className="modal-filtro-content" ref={modalRef}>
        <div className="filtro-modal-card">
          
          {/* Header */}
          <div className="filtro-modal-header">
            <div>
              <h2 className="filtro-modal-title">Filtros Avançados</h2>
              <p className="filtro-modal-subtitle">Ajuste os parâmetros para filtrar a listagem de placas</p>
            </div>
            <button className="close-button" onClick={onClose}>&times;</button>
          </div>

          {/* Body */}
          <div className="filtro-modal-body">
            
            {/* Seção de Período */}
            <div className="filtro-section">
              <h3 className="filtro-section-title">Período de Análise</h3>
              <div className="filtro-grid-4">
                <div className="filtro-field">
                  <label>Ano Inicial</label>
                  <div className="select-wrapper">
                    <select
                      value={startYear}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setStartYear(v);
                        let newMonth = startMonth;
                        if (v === maxYear && startMonth > maxMonth) {
                          newMonth = maxMonth;
                          setStartMonth(maxMonth);
                        }
                        onPreviewChange?.(v, newMonth, endYear, endMonth);
                      }}
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="filtro-field">
                  <label>Mês Inicial</label>
                  <div className="select-wrapper">
                    <select
                      value={startMonth}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setStartMonth(v);
                        onPreviewChange?.(startYear, v, endYear, endMonth);
                      }}
                    >
                      {ALLOWED_MONTHS.filter((m) => startYear < maxYear || m.num <= maxMonth).map((m) => (
                        <option key={m.num} value={m.num}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="filtro-field">
                  <label>Ano Final</label>
                  <div className="select-wrapper">
                    <select
                      value={endYear}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setEndYear(v);
                        let newMonth = endMonth;
                        if (v === maxYear && endMonth > maxMonth) {
                          newMonth = maxMonth;
                          setEndMonth(maxMonth);
                        }
                        onPreviewChange?.(startYear, startMonth, v, newMonth);
                      }}
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="filtro-field">
                  <label>Mês Final</label>
                  <div className="select-wrapper">
                    <select
                      value={endMonth}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setEndMonth(v);
                        onPreviewChange?.(startYear, startMonth, endYear, v);
                      }}
                    >
                      {ALLOWED_MONTHS.filter((m) => endYear < maxYear || m.num <= maxMonth).map((m) => (
                        <option key={m.num} value={m.num}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção de Ordenação e Resultado */}
            {!hideResultadoControls && (
            <div className="filtro-grid-2">
              
              {/* Ordenação */}
              <div className="filtro-section">
                <h3 className="filtro-section-title">Ordenação</h3>
                <div className="filtro-field">
                  <label>Ordenar listagem por</label>
                  <div className="select-wrapper">
                    <select
                      value={order}
                      onChange={(e) => setOrder(e.target.value as 'lucro' | 'prejuizo')}
                    >
                      <option value="lucro">Superávit</option>
                      <option value="prejuizo">Déficit</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Resultado Financeiro */}
              <div className="filtro-section">
                <div className="filtro-section-header">
                  <h3 className="filtro-section-title">Resultado Financeiro</h3>
                  {rangeTouched && (
                    <button type="button" className="filtro-reset-btn" onClick={handleResetRange}>
                      Redefinir
                    </button>
                  )}
                </div>
                
                <div className="range-container-new">
                  <div className="range-header-new">
                    <div className="range-values-new">
                      {rangeMinEdit ? (
                        <input
                          className="RangeValueInput"
                          value={rangeMinText}
                          onChange={(e) => setRangeMinText(e.target.value)}
                          onBlur={commitRangeMinText}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRangeMinText();
                            if (e.key === 'Escape') {
                              setRangeMinText(String(rangeMin ?? rangeMinUi));
                              setRangeMinEdit(false);
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <button
                          type="button"
                          className="range-value-btn-new"
                          onClick={() => {
                            setRangeMaxEdit(false);
                            setRangeMinText(String(rangeMin ?? rangeMinUi));
                            setRangeMinEdit(true);
                          }}
                        >
                          {formatarValor(Number(rangeMin ?? rangeMinUi))}
                        </button>
                      )}
                      
                      <span className="range-separator">até</span>
                      
                      {rangeMaxEdit ? (
                        <input
                          className="RangeValueInput"
                          value={rangeMaxText}
                          onChange={(e) => setRangeMaxText(e.target.value)}
                          onBlur={commitRangeMaxText}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRangeMaxText();
                            if (e.key === 'Escape') {
                              setRangeMaxText(String(rangeMax ?? rangeMaxUi));
                              setRangeMaxEdit(false);
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <button
                          type="button"
                          className="range-value-btn-new"
                          onClick={() => {
                            setRangeMinEdit(false);
                            setRangeMaxText(String(rangeMax ?? rangeMaxUi));
                            setRangeMaxEdit(true);
                          }}
                        >
                          {formatarValor(Number(rangeMax ?? rangeMaxUi))}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="RangeInputs">
                    <input
                      className="RangeInput"
                      type="range"
                      min={rangeMinUi}
                      max={rangeMaxUi}
                      value={Number(rangeMin ?? rangeMinUi)}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setRangeTouched(true);
                        setRangeMin(v);
                        if (rangeMax !== undefined && v > rangeMax) setRangeMax(v);
                      }}
                    />
                    <input
                      className="RangeInput"
                      type="range"
                      min={rangeMinUi}
                      max={rangeMaxUi}
                      value={Number(rangeMax ?? rangeMaxUi)}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setRangeTouched(true);
                        setRangeMax(v);
                        if (rangeMin !== undefined && v < rangeMin) setRangeMin(v);
                      }}
                    />
                  </div>
                </div>
              </div>

            </div>
            )}

            <div className="filtro-section" style={{ marginTop: '16px' }}>
              <h3 className="filtro-section-title">Cadastro no SGA</h3>
              <div className="filtro-field">
                <label>Placas exibidas</label>
                <div className="select-wrapper">
                  <select
                    value={cadastroSga ?? ''}
                    onChange={(e) => setCadastroSga((e.target.value || undefined) as CadastroSgaFiltro)}
                  >
                    <option value="">Todas</option>
                    <option value="com">Somente com cadastro no SGA</option>
                    <option value="sem">Somente sem cadastro no SGA</option>
                  </select>
                </div>
                <p className="filtro-field-hint">
                  Placas fora do cadastro do SGA têm movimento financeiro vindo de outras fontes — é o caso dos
                  veículos de terceiros, que não são cadastrados no SGA.
                </p>
              </div>
            </div>

            <div className="filtro-section" style={{ marginTop: '16px' }}>
              <h3 className="filtro-section-title">Buscar Associado</h3>
              <div className="filtro-field">
                <label>Nome ou CPF do Associado</label>
                <input
                  type="text"
                  className="premium-text-input"
                  placeholder="Buscar associado por nome ou CPF"
                  value={searchAssociado}
                  onChange={(e) => setSearchAssociado(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleApply();
                    }
                  }}
                />
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="filtro-modal-footer">
            <button type="button" className="filtro-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="filtro-btn-primary" onClick={handleApply}>
              Aplicar Filtros
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ModalFiltro;
